/**
 * Pure mapping logic for auto-filling the Banker Reconciliation from extracted document fields.
 *
 * No React / Supabase imports — fully unit-testable. Two responsibilities:
 *   1. buildAutoFillRows(): turn the edge function's `fields` + `fieldConfidence` into review rows,
 *      computing `alreadySet` per the field-class precedence (scalar blank predicate vs persisted
 *      fee-row presence vs Encompass-authoritative).
 *   2. applyAutoFillRows(): apply the rows the LO accepted into the next form-state slices, including
 *      the derived Actual Purchase Price (via a synthetic `auto_extracted_price` adjustment).
 *
 * srpPremium and investorName are evidence-only (shown for cross-check, never written). dailyRate is
 * not mapped at all.
 */

import {
  AUTO_EXTRACTED_PRICE_ID,
  INVESTOR_FEE_OPTIONS,
  toNumber,
  type CostsState,
  type ExtractedFields,
  type FieldConfidence,
  type KeyDates,
  type PricingState,
  type PurchaseAdjustment
} from './types';
import {
  BUYDOWN_AUTO_LINE_TYPE,
  BUYDOWN_BASE_LINE_TYPE,
  BUYDOWN_DISPLAY_DESCRIPTION,
  canonicalInvestorCostBaseType,
  normalizeExtractedFieldsForBuydown,
  normalizeExtractedInvestorCostType
} from '@/utils/banker-recon/buydownPassThrough';

export type AutoFillFieldKind = 'date' | 'currency' | 'integer' | 'percent' | 'text';
export type AutoFillGroup = 'warehouse' | 'investor' | 'wire' | 'investor_cost';

export interface AutoFillRow {
  /** Stable field key — warehouse fees: the line_type id; investor costs: `investorCosts[<i>]`. */
  key: string;
  label: string;
  group: AutoFillGroup;
  kind: AutoFillFieldKind;
  extractedValue: string | number;
  confidence?: number;
  currentValue: string | number | null;
  /** True when the target already holds a value (per field-class precedence). */
  alreadySet: boolean;
  /** Encompass owns this field — show as an unchecked suggestion, never default-applied. */
  encompassAuthoritative?: boolean;
  /** Shown read-only for cross-check; never written to the form. */
  evidenceOnly?: boolean;
  /** Investor-cost rows only: the mapped `INVESTOR_FEE_OPTIONS` id (the modal type-select can change it). */
  investorCostType?: string;
  /** Investor-cost rows only: raw doc wording, shown as sub-text. */
  docLabel?: string;
  /** Initial state of the Apply checkbox in the modal. */
  defaultChecked: boolean;
}

/** A provenance entry — the category-aware key `${category}:${baseType}` of an extraction-filled line item. */
export interface FilledProvenance {
  /** `${category}:${baseType}` (timestamp suffix stripped). */
  provenanceKey: string;
  confidence?: number;
}

/** Snapshot of the form state the mapper reads (assembled by the coordinator at presentation time). */
export interface AutoFillCurrentState {
  keyDates: KeyDates;
  costs: CostsState;
  pricing: PricingState;
  investorPurchaseDate: string;
  firstPaymentToInvestor: string;
  purchaseAdjustments: PurchaseAdjustment[];
  /** Current wire scalars (combined doc fills first; PA wire is fallback-only when already set). */
  wireFromInvestor: number;
  wireFromWarehouse: number;
  /** Margin-adjusted lock buy price (InvestorAdviceTab derives actual price from this + adjustments). */
  investorLockPrice: number;
  /**
   * Category-aware "already set" anchor: `${category}:${baseType}` keys for line items saved in the draft
   * (warehouse + investor). Drives fee-row precedence + investor-cost dedupe across categories.
   */
  persistedLineTypes: Set<string>;
}

export interface AutoFillApplyResult {
  keyDates: KeyDates;
  costs: CostsState;
  pricing: PricingState;
  investorPurchaseDate: string;
  firstPaymentToInvestor: string;
  purchaseAdjustments: PurchaseAdjustment[];
  wireFromInvestor: number;
  wireFromWarehouse: number;
  /** Keys of every row applied (for the toast summary). */
  filledKeys: string[];
  /** Line items filled by extraction → drives source:'extracted' provenance (warehouse + investor costs). */
  filledLineTypes: FilledProvenance[];
}

// Warehouse fee fields: ExtractedFields key -> costs.warehouse line_type id (stable, fixed ids).
const WAREHOUSE_FEE_MAP: Array<{ extractedKey: keyof ExtractedFields; lineType: string; label: string }> = [
  { extractedKey: 'warehouseInterest', lineType: 'warehouse_interest', label: 'Warehouse Interest' },
  { extractedKey: 'fundingFee', lineType: 'warehouse_funding_fee', label: 'Warehouse Funding Fee' },
  { extractedKey: 'reconciliationFee', lineType: 'warehouse_recon_fee', label: 'Reconciliation Report Fee' },
  { extractedKey: 'delegatedUWFee', lineType: 'warehouse_du_fee', label: 'Delegated Underwriter Fee' },
  { extractedKey: 'collateralDwellFee', lineType: 'collateral_dwell_fee', label: 'Collateral Dwell Fee' },
  { extractedKey: 'shortageFunded', lineType: 'shortage_funded', label: 'Shortage Funded at Closing' }
];

const isValidDateStr = (v: unknown): v is string =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

const INVESTOR_FEE_IDS = new Set<string>(INVESTOR_FEE_OPTIONS.map((o) => o.id));
/** Validate an LLM-emitted investor-cost type; fall back to `purchase_fee` for an unknown id. */
const normalizeInvestorCostType = (t: unknown, label?: unknown): string => {
  const normalized = normalizeExtractedInvestorCostType(t, label);
  return INVESTOR_FEE_IDS.has(normalized) ? normalized : 'purchase_fee';
};
const investorFeeLabel = (id: string): string =>
  INVESTOR_FEE_OPTIONS.find((o) => o.id === id)?.description ?? 'Investor Cost';

// ============================================================================
// BUILD REVIEW ROWS
// ============================================================================

export function buildAutoFillRows(
  fields: ExtractedFields,
  fieldConfidence: FieldConfidence,
  current: AutoFillCurrentState
): AutoFillRow[] {
  const normalizedFields = normalizeExtractedFieldsForBuydown(fields);
  const rows: AutoFillRow[] = [];
  const conf = (k: string): number | undefined =>
    typeof fieldConfidence[k] === 'number' ? fieldConfidence[k] : undefined;

  const pushDate = (
    key: string,
    label: string,
    group: AutoFillGroup,
    extracted: unknown,
    currentVal: string,
    opts?: { encompassAuthoritative?: boolean; confKey?: string }
  ): void => {
    if (!isValidDateStr(extracted)) {
      return;
    }
    const alreadySet = typeof currentVal === 'string' && currentVal.trim() !== '';
    const encompassAuthoritative = !!opts?.encompassAuthoritative;
    rows.push({
      key,
      label,
      group,
      kind: 'date',
      extractedValue: extracted.trim(),
      confidence: conf(opts?.confKey ?? key),
      currentValue: currentVal || null,
      alreadySet,
      encompassAuthoritative,
      defaultChecked: !alreadySet && !encompassAuthoritative
    });
  };

  const pushWire = (key: string, label: string, extracted: unknown, currentVal: number): void => {
    // Omit zero/absent wires. Scalar precedence: unset = <= 0. The coordinator fills the combined doc's
    // wires first, so when the PA's wireFromInvestor row is built the scalar is already set → unchecked.
    if (!isNum(extracted) || extracted === 0) {
      return;
    }
    const alreadySet = isNum(currentVal) && currentVal > 0;
    rows.push({
      key,
      label,
      group: 'wire',
      kind: 'currency',
      extractedValue: extracted,
      confidence: conf(key),
      currentValue: currentVal || null,
      alreadySet,
      defaultChecked: !alreadySet
    });
  };

  // --- Date scalars (unset = empty string) ---
  pushDate('fundsToEscrow', 'Funds Sent to Escrow', 'warehouse', normalizedFields.fundsToEscrowDate, current.keyDates.fundsToEscrow, { confKey: 'fundsToEscrowDate' });
  pushDate('fundsReceived', 'Funds Received (Wire)', 'warehouse', normalizedFields.fundsReceivedDate, current.keyDates.fundsReceived, { confKey: 'fundsReceivedDate' });
  pushDate('closingDate', 'Closing Date', 'warehouse', normalizedFields.closingDate, current.keyDates.closingDate, { encompassAuthoritative: true, confKey: 'closingDate' });
  pushDate('investorPurchaseDate', 'Investor Purchase Date', 'investor', normalizedFields.purchaseDate, current.investorPurchaseDate, { confKey: 'purchaseDate' });
  pushDate('firstPaymentToInvestor', 'First Payment to Investor', 'investor', normalizedFields.firstPaymentToInvestor, current.firstPaymentToInvestor, { confKey: 'firstPaymentToInvestor' });

  // --- Wires (warehouse/combined "Gross Amount Received from Investor" + "Net Proceeds to Originator") ---
  pushWire('wireFromInvestor', 'Wire from Investor', normalizedFields.wireFromInvestor, current.wireFromInvestor);
  pushWire('wireFromWarehouse', 'Wire from Warehouse', normalizedFields.wireFromWarehouse, current.wireFromWarehouse);

  // (warehouse_days is no longer extracted/applied — it's derived from the funds dates on save, Part C1)

  // --- Warehouse fee line items (anchor = persisted line-item presence; omit zero) ---
  for (const fee of WAREHOUSE_FEE_MAP) {
    const val = normalizedFields[fee.extractedKey];
    if (!isNum(val) || val === 0) {
      continue;
    }
    const alreadySet = current.persistedLineTypes.has(`warehouse_cost:${fee.lineType}`);
    const curItem = current.costs.warehouse.find((w) => w.id === fee.lineType);
    rows.push({
      key: fee.lineType,
      label: fee.label,
      group: 'warehouse',
      kind: 'currency',
      extractedValue: val,
      confidence: conf(fee.extractedKey as string),
      currentValue: curItem ? curItem.amount : null,
      alreadySet,
      defaultChecked: !alreadySet
    });
  }

  // --- Investor secondary-market costs (dynamic ids; anchor = category-aware persisted set; omit zero) ---
  const investorCosts = normalizedFields.investorCosts ?? [];
  for (let i = 0; i < investorCosts.length; i++) {
    const ic = investorCosts[i];
    if (!ic || !isNum(ic.amount) || ic.amount === 0) {
      continue;
    }
    const type = normalizeInvestorCostType(ic.type, ic.label);
    const itemKey = `investorCosts[${i}]`;
    const alreadySet = current.persistedLineTypes.has(`investor_cost:${type}`);
    rows.push({
      key: itemKey,
      label: investorFeeLabel(type),
      group: 'investor_cost',
      kind: 'currency',
      extractedValue: ic.amount,
      confidence: conf(itemKey),
      currentValue: null,
      alreadySet,
      investorCostType: type,
      docLabel: typeof ic.label === 'string' ? ic.label : undefined,
      defaultChecked: !alreadySet
    });
  }

  // --- FlexClose credit (scalar; unset = <= 0) ---
  if (isNum(normalizedFields.flexCloseCredit)) {
    const cur = current.costs.flexcloseCredit;
    const alreadySet = isNum(cur) && cur > 0;
    rows.push({
      key: 'flexcloseCredit',
      label: 'FlexClose Credit',
      group: 'warehouse',
      kind: 'currency',
      extractedValue: normalizedFields.flexCloseCredit,
      confidence: conf('flexCloseCredit'),
      currentValue: cur || null,
      alreadySet,
      defaultChecked: !alreadySet
    });
  }

  // --- Actual purchase price (percent; applied via a purchase adjustment) ---
  if (isNum(normalizedFields.targetActualPurchasePrice) && normalizedFields.targetActualPurchasePrice > 0) {
    const cur = current.pricing.actualPricePercent;
    const alreadySet = isNum(cur) && cur > 0;
    rows.push({
      key: 'targetActualPurchasePrice',
      label: 'Actual Purchase Price',
      group: 'investor',
      kind: 'percent',
      extractedValue: normalizedFields.targetActualPurchasePrice,
      confidence: conf('targetActualPurchasePrice'),
      currentValue: cur || null,
      alreadySet,
      defaultChecked: !alreadySet
    });
  }

  // --- Evidence-only rows (never applied) ---
  if (typeof normalizedFields.investorName === 'string' && normalizedFields.investorName.trim() !== '') {
    rows.push({
      key: 'investorName',
      label: 'Investor (from document)',
      group: 'investor',
      kind: 'text',
      extractedValue: normalizedFields.investorName.trim(),
      confidence: conf('investorName'),
      currentValue: null,
      alreadySet: false,
      evidenceOnly: true,
      defaultChecked: false
    });
  }
  if (isNum(normalizedFields.srpPremium)) {
    rows.push({
      key: 'srpPremium',
      label: 'SRP Premium (from document)',
      group: 'investor',
      kind: 'currency',
      extractedValue: normalizedFields.srpPremium,
      confidence: conf('srpPremium'),
      currentValue: null,
      alreadySet: false,
      evidenceOnly: true,
      defaultChecked: false
    });
  }

  return rows;
}

// ============================================================================
// APPLY ACCEPTED ROWS
// ============================================================================

/** Upsert the synthetic auto_extracted_price adjustment so actual price == target. Replaces in place. */
function upsertAutoPriceAdjustment(
  adjustments: PurchaseAdjustment[],
  target: number,
  investorLockPrice: number
): PurchaseAdjustment[] {
  const entry: PurchaseAdjustment = {
    id: AUTO_EXTRACTED_PRICE_ID,
    description: 'Actual Purchase Price (from document)',
    basisPoints: round6(target - investorLockPrice),
    extractedTargetPrice: target
  };
  const exists = adjustments.some((a) => a.id === AUTO_EXTRACTED_PRICE_ID);
  return exists
    ? adjustments.map((a) => (a.id === AUTO_EXTRACTED_PRICE_ID ? entry : a))
    : [...adjustments, entry];
}

export function applyAutoFillRows(
  current: AutoFillCurrentState,
  acceptedRows: AutoFillRow[]
): AutoFillApplyResult {
  const keyDates: KeyDates = { ...current.keyDates };
  const costs: CostsState = {
    ...current.costs,
    warehouse: current.costs.warehouse.map((w) => ({ ...w })),
    investor: current.costs.investor.map((i) => ({ ...i }))
  };
  const pricing: PricingState = { ...current.pricing };
  let investorPurchaseDate = current.investorPurchaseDate;
  let firstPaymentToInvestor = current.firstPaymentToInvestor;
  let purchaseAdjustments = current.purchaseAdjustments.map((a) => ({ ...a }));
  let wireFromInvestor = current.wireFromInvestor;
  let wireFromWarehouse = current.wireFromWarehouse;
  const filledKeys: string[] = [];
  const filledLineTypes: FilledProvenance[] = [];

  for (const row of acceptedRows) {
    if (row.evidenceOnly) {
      continue;
    }
    switch (row.key) {
      case 'fundsToEscrow':
        keyDates.fundsToEscrow = String(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'fundsReceived':
        keyDates.fundsReceived = String(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'closingDate':
        keyDates.closingDate = String(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'wireFromInvestor':
        wireFromInvestor = toNumber(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'wireFromWarehouse':
        wireFromWarehouse = toNumber(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'flexcloseCredit':
        costs.flexcloseCredit = toNumber(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'investorPurchaseDate':
        investorPurchaseDate = String(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'firstPaymentToInvestor':
        firstPaymentToInvestor = String(row.extractedValue);
        filledKeys.push(row.key);
        break;
      case 'targetActualPurchasePrice': {
        const target = toNumber(row.extractedValue);
        purchaseAdjustments = upsertAutoPriceAdjustment(purchaseAdjustments, target, current.investorLockPrice);
        // Set immediately too so there's no stale-render window; the InvestorAdviceTab derive recomputes
        // the identical value (investorLockPrice + Σ adjustments == target).
        pricing.actualPricePercent = target;
        filledKeys.push(row.key);
        break;
      }
      default: {
        // Investor secondary-market cost row (key === `investorCosts[<i>]`).
        if (row.group === 'investor_cost' && row.investorCostType) {
          const type = normalizeInvestorCostType(row.investorCostType, row.docLabel);
          // Dedupe by base type against the working investor list (saved+loaded+already-applied this batch),
          // matching the form's one-per-base-type convention — never add a second cost of the same type.
          if (costs.investor.some((i) => canonicalInvestorCostBaseType(i.id, i.description) === type)) {
            break;
          }
          const isBuydown = type === BUYDOWN_BASE_LINE_TYPE;
          costs.investor.push({
            id: isBuydown ? BUYDOWN_AUTO_LINE_TYPE : `${type}_${Date.now()}`,
            description: isBuydown ? BUYDOWN_DISPLAY_DESCRIPTION : investorFeeLabel(type),
            amount: toNumber(row.extractedValue)
          });
          filledKeys.push(row.key);
          filledLineTypes.push({ provenanceKey: `investor_cost:${type}`, confidence: row.confidence });
          break;
        }
        // Warehouse fee row: key === line_type id.
        const fee = WAREHOUSE_FEE_MAP.find((f) => f.lineType === row.key);
        if (!fee) {
          break;
        }
        const amount = toNumber(row.extractedValue);
        const idx = costs.warehouse.findIndex((w) => w.id === fee.lineType);
        if (idx >= 0) {
          costs.warehouse[idx] = { ...costs.warehouse[idx], amount };
        } else {
          costs.warehouse.push({ id: fee.lineType, description: fee.label, amount });
        }
        filledKeys.push(row.key);
        filledLineTypes.push({ provenanceKey: `warehouse_cost:${fee.lineType}`, confidence: row.confidence });
        break;
      }
    }
  }

  return {
    keyDates,
    costs,
    pricing,
    investorPurchaseDate,
    firstPaymentToInvestor,
    purchaseAdjustments,
    wireFromInvestor,
    wireFromWarehouse,
    filledKeys,
    filledLineTypes
  };
}

// ============================================================================
// PRICE-PIN HELPER (used by InvestorAdviceTab's derive effect)
// ============================================================================

/**
 * Re-pin any `auto_extracted_price` adjustment's basisPoints to (extractedTargetPrice - investorLockPrice)
 * so the derived Actual Purchase Price stays equal to the document's target even when the lock price moves.
 * Returns the SAME array reference when nothing changed (so a React effect won't loop).
 */
export function recomputeAutoPriceBasis(
  adjustments: PurchaseAdjustment[],
  investorLockPrice: number
): PurchaseAdjustment[] {
  let changed = false;
  const next = adjustments.map((a) => {
    if (a.id === AUTO_EXTRACTED_PRICE_ID && typeof a.extractedTargetPrice === 'number') {
      const basisPoints = round6(a.extractedTargetPrice - investorLockPrice);
      if (basisPoints !== a.basisPoints) {
        changed = true;
        return { ...a, basisPoints };
      }
    }
    return a;
  });
  return changed ? next : adjustments;
}
